/**
 * useCircuitBreaker.ts
 * 熔断器机制：连续 3 次 AI 交互错误后打开熔断器，阻止进一步请求
 * 用户可选择重试或中断（解锁输入框并清空错误状态）
 */

import { useState, useCallback, useRef } from 'react';
import type { CircuitBreakerState, EditorSelectionContext } from '../types';

/** 触发熔断的连续错误阈值 */
const CIRCUIT_BREAKER_THRESHOLD = 3;

export interface CircuitBreakerHandle {
  state: CircuitBreakerState;
  /** 记录一次错误，自动判断是否触发熔断 */
  recordError: (type: string, message: string, userInput: string, contextBlocks?: EditorSelectionContext[]) => void;
  /** 记录一次成功，重置计数器 */
  recordSuccess: () => void;
  /** 手动重置熔断器（用户点击"中断"时调用） */
  reset: () => void;
  /** 用户点击"重试"时的回调引用 */
  onRetryRef: React.MutableRefObject<(() => void) | null>;
  /** 用户点击"中断"时的回调引用 */
  onCancelRef: React.MutableRefObject<(() => void) | null>;
}

export function useCircuitBreaker(): CircuitBreakerHandle {
  const [state, setState] = useState<CircuitBreakerState>({
    isOpen: false,
    errorCount: 0,
    lastUserInput: '',
    lastContextBlocks: undefined,
    consecutiveErrors: [],
  });

  const onRetryRef = useRef<(() => void) | null>(null);
  const onCancelRef = useRef<(() => void) | null>(null);

  const recordError = useCallback((
    type: string,
    message: string,
    userInput: string,
    contextBlocks?: EditorSelectionContext[]
  ): void => {
    setState(prev => {
      const newCount = prev.errorCount + 1;
      const newErrors = [
        ...prev.consecutiveErrors,
        { type, message },
      ];
      return {
        isOpen: newCount >= CIRCUIT_BREAKER_THRESHOLD,
        errorCount: newCount,
        lastUserInput: userInput || prev.lastUserInput,
        lastContextBlocks: contextBlocks ?? prev.lastContextBlocks,
        consecutiveErrors: newErrors,
      };
    });
  }, []);

  const recordSuccess = useCallback((): void => {
    setState({
      isOpen: false,
      errorCount: 0,
      lastUserInput: '',
      lastContextBlocks: undefined,
      consecutiveErrors: [],
    });
  }, []);

  const reset = useCallback((): void => {
    setState({
      isOpen: false,
      errorCount: 0,
      lastUserInput: '',
      lastContextBlocks: undefined,
      consecutiveErrors: [],
    });
  }, []);

  return { state, recordError, recordSuccess, reset, onRetryRef, onCancelRef };
}
